import { useEffect, useMemo, useState } from 'react';
import {
  GetMyProjectActivityFeedQueryData,
  useGetMyProjectActivityFeedQuery,
  useGetMyProjectActivitySummaryQuery,
} from '@/generated/graphql';
import { utcYearRange } from './activityCalendar';

const PAGE_SIZE = 20;
type ActivityItem =
  GetMyProjectActivityFeedQueryData['myProjectActivityFeed']['items'][number];

export function useProjectActivity(initialYear = new Date().getUTCFullYear()) {
  const [year, setYear] = useState(initialYear);
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<ActivityItem[]>([]);
  const range = useMemo(() => utcYearRange(year), [year]);

  const summaryQuery = useGetMyProjectActivitySummaryQuery({
    variables: range,
    fetchPolicy: 'cache-and-network',
  });
  const feedQuery = useGetMyProjectActivityFeedQuery({
    variables: {
      ...range,
      pagination: { page, pageSize: PAGE_SIZE },
      sort: {
        field: 'OCCURRED_AT',
        direction: 'DESC',
      },
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    setPage(1);
    setItems([]);
  }, [year]);

  useEffect(() => {
    const incoming = feedQuery.data?.myProjectActivityFeed.items;
    if (!incoming) return;
    setItems((current) => {
      if (page === 1) return incoming;
      const byId = new Map(current.map((item) => [item.id, item]));
      incoming.forEach((item) => byId.set(item.id, item));
      return [...byId.values()];
    });
  }, [feedQuery.data, page]);

  const pagination = feedQuery.data?.myProjectActivityFeed.pagination;

  return {
    year,
    setYear,
    summary: summaryQuery.data?.myProjectActivitySummary,
    summaryLoading: summaryQuery.loading,
    summaryError: summaryQuery.error,
    retrySummary: summaryQuery.refetch,
    items,
    feedLoading: feedQuery.loading,
    feedError: feedQuery.error,
    retryFeed: feedQuery.refetch,
    loadMore: () => setPage((current) => current + 1),
    hasMore: Boolean(
      pagination && pagination.currentPage < pagination.totalPages
    ),
  };
}
